import React from 'react';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { toast } from 'react-toastify';
import type { DropdownOption } from '../../../../types/DesignSystem/Dropdown';
import { EnvironmentBasicInfoFormData } from '../Types/env.types';
import TooltipOverlay from 'Components/Common/components/Button/TooltipOverlay';

interface EnvironmentBasicInfoTabProps {
  editData: EnvironmentBasicInfoFormData;
  onFormChange: (data: EnvironmentBasicInfoFormData) => void;
  existingEnvironmentNames: string[];
  currentEnvironmentName?: string;
  onShowDisableDialog: () => void;
  // onShowProdDialog: () => void;
  hasProductionEnv: boolean;
  isProduction: boolean;
}

const DEFAULT_CAROUSEL_DESKTOP = 5;
const DEFAULT_CAROUSEL_TABLET = 3;
const DEFAULT_CAROUSEL_MOBILE = 2;

const EnvironmentBasicInfoTab: React.FC<EnvironmentBasicInfoTabProps> = ({
  editData,
  onFormChange,
  existingEnvironmentNames,
  currentEnvironmentName,
  onShowDisableDialog,
  // onShowProdDialog,
  hasProductionEnv,
  isProduction
}) => {
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const setValue = (key: keyof EnvironmentBasicInfoFormData, value: any) => {
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }

    onFormChange({
      ...editData,
      [key]: value,
    });
  };

  const validateURL = (url: string, fieldName: string): string | null => {
    if (!url) return null;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return 'Invalid URL format. Must include http:// or https://';
    }

    try {
      const urlObj = new URL(url);
      if (!urlObj.protocol.startsWith('http')) {
        return 'Invalid URL format. Must include http:// or https://';
      }

      // Check if hostname exists
      if (!urlObj.hostname || urlObj.hostname.trim().length === 0) {
        return 'Invalid URL format. Domain is required';
      }

      // Check for valid TLD - domain must have at least one character after the dot
      if (!urlObj.hostname.includes('.') || urlObj.hostname.endsWith('.')) {
        return 'Invalid URL format. Domain is required';
      }

      // Check if domain has content after the last dot (TLD check)
      const parts = urlObj.hostname.split('.');
      if (parts[parts.length - 1].trim().length === 0) {
        return 'Invalid URL format. Domain is required';
      }

      // Domain validation pattern
      const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!domainPattern.test(urlObj.hostname)) {
        return 'Invalid URL format. Url contains invalid characters';
      }
      return null;
    } catch {
      return 'Invalid URL format.';
    }
  };

  const validateEnvironmentName = (name: string): string | null => {
    if (!name || name.trim().length === 0) {
      return 'Environment name is required';
    }
    if (name.length < 1 || name.length > 100) {
      return 'Environment name must be 1-100 characters';
    }
    const validPattern = /^[a-zA-Z0-9\s\-_]+$/;
    if (!validPattern.test(name)) {
      return 'Environment name can only contain letters, numbers, spaces, hyphens, and underscores';
    }

    const isDuplicate = existingEnvironmentNames.some(
      (existingName) =>
        existingName.toLowerCase() === name.toLowerCase() &&
        existingName.toLowerCase() !== currentEnvironmentName?.toLowerCase()
    );

    if (isDuplicate) {
      return 'This environment name already exists in this channel';
    }

    return null;
  };

  const validateAuthToken = (token: string): string | null => {
    if (!token || token.trim().length === 0) {
      return 'Authentication token is required';
    }
    const alphanumericPattern = /^[a-zA-Z0-9]+$/;
    if (!alphanumericPattern.test(token)) {
      return 'Authentication token must be alphanumeric';
    }
    return null;
  };

  const handleBlur = (field: keyof EnvironmentBasicInfoFormData) => {
    let error: string | null = null;
    const valueToValidate = editData[field];

    switch (field) {
      case 'name':
        error = validateEnvironmentName(valueToValidate as string);
        break;
      case 'merchant_ecommerce_domain_url':
        if (!valueToValidate || (valueToValidate as string).trim().length === 0) {
          error = 'Merchant ecommerce Domain URL is required';
        } else {
          error = validateURL(valueToValidate as string, 'Merchant Domain URL');
        }
        break;
      case 'webhook_url':
        if (valueToValidate && (valueToValidate as string).trim().length > 0) {
          error = validateURL(valueToValidate as string, 'Webhook URL');
        }
        break;
      case 'authentication_token':
        error = validateAuthToken((valueToValidate as string) || '');
        break;
      case 'storefront_api_base_url':
        if (!valueToValidate || (valueToValidate as string).trim().length === 0) {
          error = 'Store Front API URL is required';
        } else {
          error = validateURL(valueToValidate as string, 'Store Front API URL');
        }
        break;
      case 'platform':
        if (!valueToValidate || valueToValidate === 'Please Select') {
          error = 'Please select a platform';
        }
        break;
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error as string }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEnableCompatioAppsChange = (checked: boolean) => {
    if (!checked) {
      onShowDisableDialog();
      return;
    }

    const hasAllRequiredFields =
      editData.name?.trim() &&
      editData.platform && editData.platform !== 'Please Select' &&
      editData.authentication_token?.trim() &&
      editData.storefront_api_base_url?.trim() &&
      editData.merchant_ecommerce_domain_url?.trim();

    if (!hasAllRequiredFields) {
      toast.error('Please complete mandatory fields before enabling');
      return;
    }

    setValue('enable_compatio_apps', true);
  };

  const handleProdEnvironmentChange = (checked: boolean) => {
    if (checked) {
      // onShowProdDialog();
      setValue('is_prod', true);
    } else {
      setValue('is_prod', false);
    }
  };

  const handleCarouselUseDefaultChange = (checked: boolean) => {
    if (checked) {
      onFormChange({
        ...editData,
        carousel_display_settings_desktop: DEFAULT_CAROUSEL_DESKTOP,
        carousel_display_settings_tablet: DEFAULT_CAROUSEL_TABLET,
        carousel_display_settings_mobile: DEFAULT_CAROUSEL_MOBILE,
      });
    }
  };

  const isCarouselDefault =
    editData.carousel_display_settings_desktop === DEFAULT_CAROUSEL_DESKTOP &&
    editData.carousel_display_settings_tablet === DEFAULT_CAROUSEL_TABLET &&
    editData.carousel_display_settings_mobile === DEFAULT_CAROUSEL_MOBILE;

  const handleCopyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${fieldName} copied to clipboard!`);
    }).catch(() => {
      toast.error(`Failed to copy ${fieldName}`);
    });
  };

  const showThirdPartyHelp = editData.third_party_price_engine_integration === 'Yes';

  return (
    <div className="tw-space-y-6">
      <div className="tw-flex tw-items-center tw-justify-between">
        <FieldTitle label="Enable Compatio Apps" infoMessage='Master switch to enable or disable all Compatio features (Commend and Guide). When disabled, all tabs become inactive and features are turned off on your e-commerce site.'></FieldTitle>
        <Toggle
          label=""
          checked={editData.enable_compatio_apps}
          onChange={handleEnableCompatioAppsChange}
        />
      </div>

      <div>
        <TextField
          label="Environment Name"
          placeholder="Enter environment name"
          value={editData.name}
          onChange={(value) => {
            setValue('name', value);
            const error = validateEnvironmentName(value);
            if (error) {
              setErrors((prev) => ({ ...prev, name: error }));
            } else {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.name;
                return newErrors;
              });
            }
          }}
          onBlur={() => handleBlur('name')}
          error={!!errors.name}
          helperText={errors.name}
          infoMessage='Unique name to identify this environment (e.g., "Production", "Staging", "Development"). Must be unique within your channel. Use descriptive names to easily distinguish between environments.'
          required
        />
      </div>

      <div>
        <Dropdown
          label="Platform"
          placeholder="Select platform"
          value={editData.platform || 'Please Select'}
          onChange={(value) => {
            onFormChange({
              ...editData,
              platform: String(value),
            });

            const error = value === 'Please Select' ? 'Please select a platform' : null;
            if (error) {
              setErrors((prev) => ({ ...prev, platform: error }));
            } else {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.platform;
                return newErrors;
              });
            }
          }}
          options={[
            { value: 'Please Select', label: 'Please Select' },
            { value: 'Magento', label: 'Magento' },
            { value: 'Shopify', label: 'Shopify' },
            { value: 'BigCommerce', label: 'BigCommerce' },
            { value: 'Custom', label: 'Custom' },
          ] as DropdownOption[]}
          error={!!errors.platform}
          infoMessage='Select your e-commerce platform type. This helps us configure the correct integration settings and API endpoints for your platform.'
          helperText={errors.platform || ''}
          required
        />
      </div>

      <div>
        <TextField
          label="Platform Environment Key"
          placeholder="Platform environment key"
          value={editData.channel_key}
          disabled
          className="tw-w-full"
          required
          showCopyButton
          onCopy={() =>
            handleCopyToClipboard(
              editData.channel_key || '',
              'Platform Key'
            )
          }
          helperText="Copy this key to your e-commerce platform admin panel"
        />
      </div>

      <div>
        <TextField
          label="Merchant ecommerce Domain URL"
          placeholder="https://example.com"
          value={editData.merchant_ecommerce_domain_url || ''}
          onChange={(value) => {
            setValue('merchant_ecommerce_domain_url', value);
            let error: string | null = null;
            if (!value || value.trim().length === 0) {
              error = 'Merchant ecommerce Domain URL is required';
            } else {
              error = validateURL(value, 'Merchant Domain URL');
            }

            if (error) {
              setErrors((prev) => ({ ...prev, merchant_ecommerce_domain_url: error! }));
            } else {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.merchant_ecommerce_domain_url;
                return newErrors;
              });
            }
          }}
          onBlur={() => handleBlur('merchant_ecommerce_domain_url')}
          error={!!errors.merchant_ecommerce_domain_url}
          helperText={errors.merchant_ecommerce_domain_url || ''}
          infoMessage='The base URL of your e-commerce website (e.g., https://www.yourstore.com). Used for analytics, tracking and linking back to your store.'
          required
        />
      </div>

      <div>
        <TextField
          label="Webhook URL"
          placeholder="https://webhook.example.com"
          value={editData.webhook_url || ''}
          onChange={(value) => {
            setValue('webhook_url', value);
            if (value && value.trim().length > 0) {
              const error = validateURL(value, 'Webhook URL');
              if (error) {
                setErrors((prev) => ({ ...prev, webhook_url: error }));
              } else {
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.webhook_url;
                  return newErrors;
                });
              }
            } else {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.webhook_url;
                return newErrors;
              });
            }
          }}
          onBlur={() => handleBlur('webhook_url')}
          className="tw-w-full"
          showCopyButton
          onCopy={() =>
            handleCopyToClipboard(editData.webhook_url || '', 'Webhook URL')
          }
          error={!!errors.webhook_url}
          helperText={errors.webhook_url || 'Configure this URL in your e-commerce platform to receive updates'}
        />
      </div>

      <div>
        <TextField
          label="Authentication Token"
          placeholder="Enter authentication token"
          value={editData.authentication_token || ''}
          onChange={(value) => {
            setValue('authentication_token', value);
            const error = validateAuthToken(value);
            if (error) {
              setErrors((prev) => ({ ...prev, authentication_token: error }));
            } else {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.authentication_token;
                return newErrors;
              });
            }
          }}
          onBlur={() => handleBlur('authentication_token')}
          error={!!errors.authentication_token}
          helperText={errors.authentication_token || 'Generate this token in your e-commerce admin panel and paste it here'}
          required
        />
      </div>

      <div>
        <TextField
          label="Store Front API URL"
          placeholder="https://api.example.com"
          value={editData.storefront_api_base_url || ''}
          onChange={(value) => {
            setValue('storefront_api_base_url', value);
            let error: string | null = null;
            if (!value || value.trim().length === 0) {
              error = 'Store Front API URL is required';
            } else {
              error = validateURL(value, 'Store Front API URL');
            }

            if (error) {
              setErrors((prev) => ({ ...prev, storefront_api_base_url: error! }));
            } else {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.storefront_api_base_url;
                return newErrors;
              });
            }
          }}
          onBlur={() => handleBlur('storefront_api_base_url')}
          error={!!errors.storefront_api_base_url}
          helperText={errors.storefront_api_base_url}
          infoMessage="Your e-commerce platform's API endpoint for real-time product data"
          required
        />
      </div>

      <div>
        <FieldTitle label="Carousel Display Settings" infoMessage='Number of products visible in recommendation carousels on this device type. Customers can scroll to see more products.' className='tw-pb-2' />
        <div className="tw-flex tw-gap-3">
          <div className="tw-flex-1">
            <Dropdown
              label="Desktop"
              value={editData.carousel_display_settings_desktop?.toString() || '5'}
              onChange={(value) => setValue('carousel_display_settings_desktop', parseInt(String(value), 10))}
              options={[
                { value: '4', label: '4' },
                { value: '5', label: '5' },
              ] as DropdownOption[]}
            />
          </div>
          <div className="tw-flex-1">
            <Dropdown
              label="Tablet"
              value={editData.carousel_display_settings_tablet?.toString() || '3'}
              onChange={(value) => setValue('carousel_display_settings_tablet', parseInt(String(value), 10))}
              options={[
                { value: '2', label: '2' },
                { value: '3', label: '3' },
                { value: '4', label: '4' },
              ] as DropdownOption[]}
            />
          </div>
          <div className="tw-flex-1">
            <Dropdown
              label="Mobile"
              value={editData.carousel_display_settings_mobile?.toString() || '2'}
              onChange={(value) => setValue('carousel_display_settings_mobile', parseInt(String(value), 10))}
              options={[
                { value: '1', label: '1' },
                { value: '2', label: '2' },
                { value: '3', label: '3' },
              ] as DropdownOption[]}
            />
          </div>
        </div>
        <div className="tw-mt-3">
          <Checkbox
            label="Use default value"
            checked={isCarouselDefault}
            onChange={handleCarouselUseDefaultChange}
          />
        </div>
      </div>

      <div>
        <FieldTitle label="Advanced Settings" infoMessage='Configure advanced integration options for enterprise features.' className='tw-pb-2' />
        <div>
          <Dropdown
            label="Third Party Price Engine Integration"
            infoMessage='Enable if you use a third-party pricing engine (e.g., Pricefx, PROS). Requires additional configuration in Global Management System.'
            placeholder="Select price engine"
            value={editData.third_party_price_engine_integration}
            onChange={(value) => setValue('third_party_price_engine_integration', value)}
            options={[
              { value: 'No', label: 'No' },
              { value: 'Yes', label: 'Yes' },
            ] as DropdownOption[]}
            helperText={showThirdPartyHelp ? "To integrate the Product Price Engine (PE) with the Compatio widgets, please connect with Compatio support and provide the required information, such as the PE endpoint and its parameters." : ""}
          />
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-gap-3 tw-pt-4 tw-border-t tw-border-grey-200">
        <Checkbox
          label="Use as 'Production Environment'"
          checked={editData.is_prod}
          onChange={handleProdEnvironmentChange}
          disabled={!isProduction && hasProductionEnv}
        />
      </div>
    </div>
  );
};

export default EnvironmentBasicInfoTab;